<template>
  <div>
    <v-card v-if="editor">
      <!-- Toolbar -->
      <div class="editor-toolbar pa-2">
        <!-- History -->
        <div class="toolbar-group">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click="editor.chain().focus().undo().run()"
                :disabled="!editor.can().chain().focus().undo().run()"
              >
                <v-icon small>mdi-undo</v-icon>
              </v-btn>
            </template>
            <span>Rückgängig</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click="editor.chain().focus().redo().run()"
                :disabled="!editor.can().chain().focus().redo().run()"
              >
                <v-icon small>mdi-redo</v-icon>
              </v-btn>
            </template>
            <span>Wiederholen</span>
          </v-tooltip>
        </div>

        <v-divider vertical class="toolbar-divider"></v-divider>

        <!-- Text formatting -->
        <div class="toolbar-group">
          <v-btn-toggle dense multiple>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().toggleBold().run()"
                  :disabled="!editor.can().chain().focus().toggleBold().run()"
                  :class="{ 'primary--text': editor.isActive('bold') }"
                >
                  <v-icon small>mdi-format-bold</v-icon>
                </v-btn>
              </template>
              <span>Fett</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().toggleItalic().run()"
                  :disabled="!editor.can().chain().focus().toggleItalic().run()"
                  :class="{ 'primary--text': editor.isActive('italic') }"
                >
                  <v-icon small>mdi-format-italic</v-icon>
                </v-btn>
              </template>
              <span>Kursiv</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().toggleUnderline().run()"
                  :class="{ 'primary--text': editor.isActive('underline') }"
                >
                  <v-icon small>mdi-format-underline</v-icon>
                </v-btn>
              </template>
              <span>Unterstrichen</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().toggleStrike().run()"
                  :disabled="!editor.can().chain().focus().toggleStrike().run()"
                  :class="{ 'primary--text': editor.isActive('strike') }"
                >
                  <v-icon small>mdi-format-strikethrough</v-icon>
                </v-btn>
              </template>
              <span>Durchgestrichen</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().toggleHighlight().run()"
                  :class="{ 'primary--text': editor.isActive('highlight') }"
                >
                  <v-icon small>mdi-marker</v-icon>
                </v-btn>
              </template>
              <span>Markieren</span>
            </v-tooltip>
          </v-btn-toggle>
        </div>

        <v-divider vertical class="toolbar-divider"></v-divider>

        <!-- Headings & Paragraph -->
        <div class="toolbar-group">
          <v-btn-toggle dense multiple>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 1 }).run()
                  "
                  :class="{
                    'primary--text': editor.isActive('heading', { level: 1 }),
                  }"
                >
                  <v-icon small>mdi-format-header-1</v-icon>
                </v-btn>
              </template>
              <span>Überschrift 1</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                  "
                  :class="{
                    'primary--text': editor.isActive('heading', { level: 2 }),
                  }"
                >
                  <v-icon small>mdi-format-header-2</v-icon>
                </v-btn>
              </template>
              <span>Überschrift 2</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 3 }).run()
                  "
                  :class="{
                    'primary--text': editor.isActive('heading', { level: 3 }),
                  }"
                >
                  <v-icon small>mdi-format-header-3</v-icon>
                </v-btn>
              </template>
              <span>Überschrift 3</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().setParagraph().run()"
                  :class="{ 'primary--text': editor.isActive('paragraph') }"
                >
                  <v-icon small>mdi-format-paragraph</v-icon>
                </v-btn>
              </template>
              <span>Absatz</span>
            </v-tooltip>
          </v-btn-toggle>
        </div>

        <v-divider vertical class="toolbar-divider"></v-divider>

        <!-- Text Align -->
        <div class="toolbar-group">
          <v-btn-toggle dense>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().setTextAlign('left').run()"
                  :class="{
                    'primary--text': editor.isActive({ textAlign: 'left' }),
                  }"
                >
                  <v-icon small>mdi-format-align-left</v-icon>
                </v-btn>
              </template>
              <span>Linksbündig</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().setTextAlign('center').run()"
                  :class="{
                    'primary--text': editor.isActive({ textAlign: 'center' }),
                  }"
                >
                  <v-icon small>mdi-format-align-center</v-icon>
                </v-btn>
              </template>
              <span>Zentriert</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().setTextAlign('right').run()"
                  :class="{
                    'primary--text': editor.isActive({ textAlign: 'right' }),
                  }"
                >
                  <v-icon small>mdi-format-align-right</v-icon>
                </v-btn>
              </template>
              <span>Rechtsbündig</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().setTextAlign('justify').run()"
                  :class="{
                    'primary--text': editor.isActive({ textAlign: 'justify' }),
                  }"
                >
                  <v-icon small>mdi-format-align-justify</v-icon>
                </v-btn>
              </template>
              <span>Blocksatz</span>
            </v-tooltip>
          </v-btn-toggle>
        </div>

        <v-divider vertical class="toolbar-divider"></v-divider>

        <!-- Lists -->
        <div class="toolbar-group">
          <v-btn-toggle dense multiple>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().toggleBulletList().run()"
                  :class="{ 'primary--text': editor.isActive('bulletList') }"
                >
                  <v-icon small>mdi-format-list-bulleted</v-icon>
                </v-btn>
              </template>
              <span>Aufzählungsliste</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().toggleOrderedList().run()"
                  :class="{ 'primary--text': editor.isActive('orderedList') }"
                >
                  <v-icon small>mdi-format-list-numbered</v-icon>
                </v-btn>
              </template>
              <span>Nummerierte Liste</span>
            </v-tooltip>
          </v-btn-toggle>
        </div>

        <v-divider vertical class="toolbar-divider"></v-divider>

        <!-- Code & Blockquote -->
        <div class="toolbar-group">
          <v-btn-toggle dense multiple>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().toggleCode().run()"
                  :disabled="!editor.can().chain().focus().toggleCode().run()"
                  :class="{ 'primary--text': editor.isActive('code') }"
                >
                  <v-icon small>mdi-code-tags</v-icon>
                </v-btn>
              </template>
              <span>Code</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().toggleCodeBlock().run()"
                  :class="{ 'primary--text': editor.isActive('codeBlock') }"
                >
                  <v-icon small>mdi-code-braces</v-icon>
                </v-btn>
              </template>
              <span>Code-Block</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="editor.chain().focus().toggleBlockquote().run()"
                  :class="{ 'primary--text': editor.isActive('blockquote') }"
                >
                  <v-icon small>mdi-format-quote-close</v-icon>
                </v-btn>
              </template>
              <span>Zitat</span>
            </v-tooltip>
          </v-btn-toggle>
        </div>

        <div class="toolbar-spacer"></div>

        <!-- Clear Formatting -->
        <div class="toolbar-group">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click="clearFormatting()"
              >
                <v-icon small>mdi-format-clear</v-icon>
              </v-btn>
            </template>
            <span>Formatierung löschen</span>
          </v-tooltip>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Editor Content -->
      <v-card-text class="pa-4">
        <editor-content :editor="editor" class="tiptap-editor" />
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import Highlight from "@tiptap/extension-highlight";
import Typography from "@tiptap/extension-typography";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";

export default {
  name: "TipTapEditor",
  components: {
    EditorContent,
  },

  props: {
    value: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      editor: null,
    };
  },

  watch: {
    value(value) {
      // HTML
      const isSame = this.editor.getHTML() === value;
      // JSON
      // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

      if (isSame) {
        return;
      }
      this.editor.commands.setContent(value, false);
    },
  },

  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
        Highlight,
        Typography,
        Underline,
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
      ],
      onUpdate: () => {
        // HTML
        this.$emit("input", this.editor.getHTML());
        // JSON
        // this.$emit('input', this.editor.getJSON())
      },
    });
  },
  methods: {
    clearFormatting() {
      this.editor.chain().focus().unsetAllMarks().run();
      this.editor.chain().focus().clearNodes().run();
    },
  },
  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>

<style lang="scss">
/* Editor Toolbar Styles */
.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.toolbar-group {
  display: flex;
  align-items: center;
  gap: 2px;
}

.toolbar-divider {
  margin: 0 8px;
  height: 24px;
  align-self: center;
}

.toolbar-spacer {
  flex-grow: 1;
  min-width: 8px;
}

/* Hide dividers on small screens to save space */
@media (max-width: 600px) {
  .toolbar-divider {
    display: none;
  }

  .editor-toolbar {
    gap: 8px;
  }
}

/* Basic editor styles */
.tiptap-editor {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  min-height: 200px;

  .ProseMirror {
    padding: 12px;
    outline: none;

    &:focus {
      border-color: var(--v-primary-base);
    }
  }
}

.tiptap {
  > * + * {
    margin-top: 0.75em;
    margin-bottom: 0.75em;
  }

  ul,
  ol {
    padding: 0 1rem;
    margin: 0.5rem 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.2;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }

  h1 {
    font-size: 2em;
  }
  h2 {
    font-size: 1.5em;
  }
  h3 {
    font-size: 1.25em;
  }

  code {
    background-color: rgba(97, 97, 97, 0.1);
    color: #616161;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-size: 0.9em;
    font-family: "Courier New", monospace;
  }

  pre {
    background: #1e1e1e;
    color: #d4d4d4;
    font-family: "Courier New", monospace;
    padding: 0.75rem 1rem;
    border-radius: 4px;
    overflow-x: auto;
    margin: 0.5rem 0;

    code {
      color: inherit;
      padding: 0;
      background: none;
      font-size: 0.875em;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
  }

  blockquote {
    padding-left: 1rem;
    border-left: 3px solid rgba(0, 0, 0, 0.12);
    margin: 1rem 0;
    font-style: italic;
    color: rgba(0, 0, 0, 0.7);
  }

  hr {
    border: none;
    border-top: 2px solid rgba(0, 0, 0, 0.1);
    margin: 2rem 0;
  }

  mark {
    background-color: #fff59d;
    padding: 0.1em 0.2em;
    border-radius: 2px;
  }

  p {
    margin: 0.5rem 0;
  }
}
</style>
